<template>
	<section class="v2rt">
		<div class="v2rt-card">
			<div class="v2rt-card__head">
				<div class="v2rt-card__icon v2rt-card__icon--blue">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>
				</div>
				<div>
					<div class="v2rt-card__title">{{ $t('campaign.status_tab') | capitalize }}</div>
					<div class="v2rt-card__sub">Live KPIs and time-series performance</div>
				</div>
			</div>

			<!-- ─── KPI Cards ─── -->
			<div class="v2rt-kpi-grid">
				<!-- Budget -->
				<div class="v2rt-kpi">
					<div class="v2rt-kpi__label">{{ $t('campaign.budget') | capitalize }}</div>
					<div class="v2rt-kpi__value">
						<span v-if="!isSSPViewer" class="v2rt-kpi__value-num">{{ spentTotal | formatMoney('') }}</span>
						<span v-if="!isSSPViewer" class="v2rt-kpi__value-sep">/</span>
						<span class="v2rt-kpi__value-total">{{ instance.budget | parseFloat }}</span>
						<span class="v2rt-kpi__value-units">{{ instance.currency | formatCurrency }}</span>
					</div>
					<div v-if="!isSSPViewer" class="v2rt-kpi__bar">
						<div class="v2rt-kpi__bar-fill" :style="{ width: budgetPercent + '%' }" />
					</div>
					<div v-if="!isSSPViewer" class="v2rt-kpi__hint">{{ budgetPercent }}% spent</div>
				</div>

				<!-- Max Bid -->
				<div class="v2rt-kpi">
					<div v-if="isBidEditModeOn && isDSP">
						<div class="v2rt-kpi__label">{{ $t('campaign.max_bid') | capitalize }}</div>
						<div class="v2rt-bid-edit">
							<input
								v-model="bidTempValue"
								class="v2rt-input"
								type="number"
								step="0.0001"
							/>
							<button class="v2rt-icon-btn v2rt-icon-btn--ok" @click="applyBidChanges">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
							</button>
							<button class="v2rt-icon-btn v2rt-icon-btn--cancel" @click="cancelBidChanges">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
							</button>
						</div>
					</div>
					<template v-else>
						<div class="v2rt-kpi__label">{{ $t('campaign.max_bid') | capitalize }}</div>
						<div class="v2rt-kpi__value">
							<span class="v2rt-kpi__value-num">{{ $options.filters.formatNumber(bidValue, 2, '') || '—' }}</span>
							<span class="v2rt-kpi__value-units">{{ instance.currency | formatCurrency }}</span>
							<button
								v-if="isDSP"
								class="v2rt-icon-btn v2rt-icon-btn--edit"
								@click="editBid"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
							</button>
						</div>
						<div
							v-if="campaignStats && campaignStats.price"
							class="v2rt-kpi__hint"
							v-html="$t('campaign.bid_desc_short', {
								min_price: $options.filters.formatCash(campaignStats.price.min, instance.currency, 2),
								max_price: $options.filters.formatCash(campaignStats.price.max, instance.currency, 2),
							})"
						/>
					</template>
				</div>

				<!-- Points -->
				<div class="v2rt-kpi">
					<div class="v2rt-kpi__label">{{ $t('campaign.points') | capitalize }}</div>
					<div class="v2rt-kpi__value">
						<svg class="v2rt-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/></svg>
						<span class="v2rt-kpi__value-num">{{ campaignStats ? campaignStats.inventory.value : '—' }}</span>
					</div>
				</div>

				<!-- Impressions -->
				<div class="v2rt-kpi">
					<div class="v2rt-kpi__label">{{ $t('campaign.impressions') | capitalize }}</div>
					<div class="v2rt-kpi__value">
						<span class="v2rt-kpi__value-num">{{ campaignStats ? (impressionsTotal | parseFloat) : '—' }}</span>
					</div>
				</div>

				<!-- Ad plays -->
				<div class="v2rt-kpi">
					<div class="v2rt-kpi__label">{{ $t('campaign.ad_plays') | capitalize }}</div>
					<div class="v2rt-kpi__value">
						<span class="v2rt-kpi__value-num">{{ campaignStats ? (adPlaysTotal | parseFloat) : '—' }}</span>
					</div>
				</div>
			</div>

			<!-- Download buttons -->
			<div class="v2rt-download-row">
				<button class="v2rt-btn v2rt-btn--outline" @click="exportExcel">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
					{{ $t('campaign.download_inventory_list') }}
				</button>
				<button
					v-if="!instance.is_request"
					class="v2rt-btn v2rt-btn--outline"
					@click="downloadBroadcastingReport"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
					{{ $t('campaign.download_broadcasting_report') }}
				</button>
			</div>

			<!-- ─── Chart Section ─── -->
			<div class="v2rt-divider" />
			<div class="v2rt-chart-wrap">
				<nice-stats
					:campaign="instance.id"
					:platforms="instance.platforms"
					:agency="instance.owner"
					:graph-data="graphData"
					:label-data="labelData"
					:impressions="impressions"
					:selector-labels="statLabels"
					:spent="spent"
					:ad-plays="adPlays"
					:format-hint="formatHint"
					:max-bid-history="maxBidHistory"
					@range-updated="rangeUpdated"
					@selected="selectedGraph = $event"
				>
					<template v-slot="statsSlot">
						<div v-if="statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over]" class="v2rt-bid-tooltip">
							<div class="v2rt-bid-tooltip__head">
								<span class="v2rt-bid-tooltip__label">{{ $t('request_list.updated') }}</span>
								<span class="v2rt-bid-tooltip__date">
									{{ formatDateMaxBid(statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].ctime) }}
									{{ formatTimeMaxBid(statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].ctime) }}
								</span>
							</div>
							<div class="v2rt-bid-tooltip__main">
								<span class="v2rt-bid-tooltip__diff">
									<span class="v2rt-bid-tooltip__old">{{ Math.round(statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].old_bid) }}</span>
									→
									<span class="v2rt-bid-tooltip__new">{{ Math.round(statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].new_bid) }}</span>
								</span>
							</div>
							<div class="v2rt-bid-tooltip__foot">
								{{ $t('campaign.by_who') }} {{ statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].created_by.first_name }} ({{ statsSlot.statsData.maxBidHistoryData[statsSlot.statsData.over].created_by.username }})
							</div>
						</div>
					</template>
				</nice-stats>
			</div>
		</div>
	</section>
</template>


<script>
import OriginalTabStatus from '@/pages/request/review/tab-status.vue';

export default {
	name: 'V2TabStatus',

	extends: OriginalTabStatus,

	computed: {
		budgetPercent() {
			if (!this.instance || !this.instance.budget) return 0;
			const spent = this.spentTotal || 0;
			const budget = this.instance.budget;
			const pct = Math.min(100, Math.round((spent / budget) * 100));
			return pct;
		},
	},
};
</script>


<style lang="sass" scoped>
@import './tab-shared'

.v2rt
	padding: 0

// ─── KPI grid ───
.v2rt-kpi-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 12px
	margin-bottom: 16px

	@media (max-width: 900px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 600px)
		grid-template-columns: 1fr

.v2rt-kpi
	border: 1px solid rgba($border, 0.6)
	border-radius: 12px
	padding: 14px 16px
	background: $card
	min-width: 0

.v2rt-kpi__label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $muted
	margin-bottom: 8px

.v2rt-kpi__value
	display: flex
	align-items: baseline
	gap: 6px
	flex-wrap: wrap

.v2rt-kpi__value-num
	font-family: $mono
	font-size: 22px
	font-weight: 700
	color: $text

.v2rt-kpi__value-sep
	font-family: $mono
	font-size: 16px
	color: $muted

.v2rt-kpi__value-total
	font-family: $mono
	font-size: 16px
	color: $muted

.v2rt-kpi__value-units
	font-size: 12px
	color: $muted
	margin-left: 2px

.v2rt-kpi__icon
	width: 16px
	height: 16px
	color: $teal
	align-self: center

.v2rt-kpi__bar
	height: 6px
	background: rgba($bg, 0.8)
	border-radius: 9999px
	margin-top: 10px
	overflow: hidden

.v2rt-kpi__bar-fill
	height: 100%
	background: $teal
	transition: width 0.3s

.v2rt-kpi__hint
	font-size: 11px
	color: $muted
	margin-top: 8px
	line-height: 1.4

// ─── Download row ───
.v2rt-download-row
	display: flex
	gap: 10px
	flex-wrap: wrap
	margin-bottom: 8px

// ─── Bid edit ───
.v2rt-bid-edit
	display: flex
	align-items: center
	gap: 6px

.v2rt-input
	flex: 1
	min-width: 0
	height: 36px
	padding: 0 12px
	border: 1px solid $border
	border-radius: 10px
	background: rgba($bg, 0.5)
	font-size: 14px
	font-family: $mono
	color: $text
	box-sizing: border-box

	&:focus
		outline: none
		border-color: $teal
		background: $card
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

.v2rt-icon-btn
	width: 28px
	height: 28px
	border-radius: 8px
	border: 1px solid $border
	background: $card
	color: $muted
	display: inline-flex
	align-items: center
	justify-content: center
	cursor: pointer
	transition: all 0.15s
	padding: 0
	flex-shrink: 0

	svg
		width: 13px
		height: 13px

	&:hover
		color: $text
		border-color: $muted

	&--ok
		color: $green
		border-color: rgba($green, 0.4)

		&:hover
			background: rgba($green, 0.06)

	&--cancel
		color: $red
		border-color: rgba($red, 0.4)

		&:hover
			background: rgba($red, 0.06)

	&--edit
		margin-left: 4px

.v2rt-divider
	height: 1px
	background: rgba($border, 0.6)
	margin: 20px 0

// ─── Chart wrap (override nice-stats) ───
.v2rt-chart-wrap
	::v-deep
		.settings
			margin-bottom: 20px

		// IMPRESSIONS / SPENT / AD_PLAYS pills
		.selectors
			display: flex
			gap: 8px
			margin-bottom: 14px
			flex-wrap: wrap

		.selectors .container
			padding: 14px 18px !important
			border-radius: 12px !important
			border: 1.5px solid $border !important
			background: $card !important
			font-family: $font !important
			cursor: pointer
			transition: all 0.15s
			min-width: 120px
			box-shadow: 0 1px 2px rgba(0,0,0,0.03)

			&:hover
				border-color: rgba($teal, 0.4) !important

			&.selected
				border-color: $teal !important
				background: rgba($teal, 0.04) !important
				box-shadow: 0 0 0 3px rgba($teal, 0.08), 0 1px 2px rgba(0,0,0,0.03)

		.selectors .ni_label--label
			font-size: 10px !important
			font-weight: 700 !important
			text-transform: uppercase !important
			letter-spacing: 0.08em !important
			color: $muted !important
			margin-bottom: 6px !important

		.selectors .value
			font-family: $mono !important
			font-size: 18px !important
			font-weight: 700 !important
			color: $text !important

		.selectors .container.selected .value
			color: $teal !important

		// Bottom row: TIME PERIOD + SCALE + platform/agency
		.dropdowns
			display: flex
			gap: 16px
			flex-wrap: wrap
			align-items: flex-end

		// All labels
		.ni_datetime--label,
		.ni_select--label,
		.ni_label--label
			font-size: 10px !important
			font-weight: 700 !important
			text-transform: uppercase !important
			letter-spacing: 0.08em !important
			color: $muted !important
			margin-bottom: 6px !important

		// Date pickers
		.nice-datetime
			min-width: 220px

		.datepicker-range
			display: flex
			gap: 8px
			align-items: center

		.field-input
			height: 40px !important
			padding: 0 12px !important
			border: 1px solid $border !important
			border-radius: 10px !important
			background: rgba($bg, 0.5) !important
			font-size: 13px !important
			font-family: $font !important
			color: $text !important

			&:focus
				border-color: $teal !important
				background: $card !important
				box-shadow: 0 0 0 3px rgba($teal, 0.12) !important

		// SCALE / Platform / Agency selects
		.scale,
		.platform
			min-width: 160px

		.nice-select
			.ni_select--select-row
				height: 40px !important
				padding: 0 12px !important
				border: 1px solid $border !important
				border-radius: 10px !important
				background: $card !important

			.ni_select--input
				font-size: 13px !important
				font-family: $font !important
				color: $text !important

		// Chart area
		.graph
			margin-top: 8px
			padding: 16px
			border: 1px solid rgba($border, 0.6)
			border-radius: 12px
			background: rgba($bg, 0.2)

		.chart-container,
		canvas
			max-width: 100%

		// Hide labels container if empty
		.dropdowns:empty
			display: none

// ─── Bid history tooltip ───
.v2rt-bid-tooltip
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 10px
	padding: 12px
	min-width: 200px
	box-shadow: 0 4px 16px rgba(0,0,0,0.08)
	font-family: $font
	font-size: 12px

.v2rt-bid-tooltip__head
	display: flex
	align-items: center
	justify-content: space-between
	gap: 12px
	padding-bottom: 8px
	border-bottom: 1px solid rgba($border, 0.5)
	margin-bottom: 8px

.v2rt-bid-tooltip__label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $muted

.v2rt-bid-tooltip__date
	font-family: $mono
	font-size: 11px
	color: $muted

.v2rt-bid-tooltip__main
	margin: 4px 0 8px

.v2rt-bid-tooltip__diff
	font-family: $mono
	font-size: 14px
	font-weight: 700

.v2rt-bid-tooltip__old
	color: $red
	text-decoration: line-through

.v2rt-bid-tooltip__new
	color: $green
	margin-left: 2px

.v2rt-bid-tooltip__foot
	font-size: 11px
	color: $muted
	padding-top: 8px
	border-top: 1px solid rgba($border, 0.5)
</style>
